import React, { useRef, useEffect, useCallback, useState } from 'react';
import './RichTextEditor.css';
import '../../styles/ampDescriptionDisplay.css';
import safeStorage from '../../utils/safeStorage';

const API_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

// Matches full AmpNebula amp URLs or relative /amps/:mfr/:slug paths pasted as text.
// Captures (manufacturerSlug, ampSlug).
const AMP_URL_PATTERN = /^(?:https?:\/\/(?:(?:www\.)?ampnebula\.com|localhost(?::\d+)?))?\/amps\/([a-z0-9-]+)\/([a-z0-9-]+)\/?$/i;

const isSvgImageFile = (file) => {
  if (!file) return false;
  return file.type === 'image/svg+xml' || file.name?.toLowerCase().endsWith('.svg');
};

// Minimal HTML entity escaper for user-supplied strings inserted into innerHTML.
function escHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── Cursor-position helpers for image keyboard deletion ───────────────────────

// Returns true when the cursor (container/offset) sits at the very start of
// `block` — i.e. there is no visible content between the block's opening edge
// and the cursor. Used so that Backspace can remove a preceding image instead
// of triggering the browser's default block-merge behaviour.
function isAtBlockStart(container, offset, block) {
  if (offset > 0) return false;
  let cur = container;
  while (cur && cur !== block) {
    const parent = cur.parentNode;
    if (!parent) return false;
    const siblings = parent.childNodes;
    for (let i = 0; i < siblings.length; i++) {
      if (siblings[i] === cur) break;
      const s = siblings[i];
      if (s.nodeType === Node.TEXT_NODE && s.textContent.length > 0) return false;
      if (s.nodeType === Node.ELEMENT_NODE) return false;
    }
    cur = parent;
  }
  return cur === block;
}

// Returns true when the cursor is at the very end of `block`.
// Used so that Delete can remove a following image.
function isAtBlockEnd(container, offset, block) {
  const len = container.nodeType === Node.TEXT_NODE
    ? container.textContent.length
    : (container.childNodes ? container.childNodes.length : 0);
  if (offset < len) return false;
  let cur = container;
  while (cur && cur !== block) {
    const parent = cur.parentNode;
    if (!parent) return false;
    const siblings = parent.childNodes;
    let found = false;
    for (let i = 0; i < siblings.length; i++) {
      if (found) {
        const s = siblings[i];
        if (s.nodeType === Node.TEXT_NODE && s.textContent.length > 0) return false;
        if (s.nodeType === Node.ELEMENT_NODE) return false;
      }
      if (siblings[i] === cur) found = true;
    }
    cur = parent;
  }
  return cur === block;
}

const RichTextEditor = ({ 
  value = '', 
  onChange, 
  readOnly = false, 
  placeholder = "Enter text...",
  amplifierId,   // when provided, images are uploaded as artifacts instead of embedded as base64
  token,         // auth token for artifact uploads
  onPasteImage   // optional: called with a File when user pastes an image (e.g. to route it to an attachment slot)
}) => {
  const editorRef = useRef(null);
  const lastInsertedNodeRef = useRef(null);    // most recently inserted img or amp-embed-card
  const canUndoImageInsertRef = useRef(false); // cleared by the next regular edit
  const savedSelectionRef = useRef(null);      // selection saved before toolbar select steals focus
  const [imageUploading, setImageUploading] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [showArtifactPicker, setShowArtifactPicker] = useState(false);
  const [pickerArtifacts, setPickerArtifacts] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [formatState, setFormatState] = useState({
    bold: false,
    italic: false,
    underline: false,
    formatBlock: 'div',
    fontSize: '',
    fontFamily: ''
  });

  // Initialize content
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  // Update format state based on current selection
  const updateFormatState = useCallback(() => {
    if (readOnly) return;

    try {
      const newState = {
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        formatBlock: 'div',
        fontSize: '',
        fontFamily: ''
      };

      // Check for heading elements and font styles
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        let element = selection.getRangeAt(0).commonAncestorContainer;
        
        // Traverse up to find element node
        while (element && element.nodeType !== Node.ELEMENT_NODE) {
          element = element.parentNode;
        }
        
        if (element) {
          // Walk up to find the nearest heading ancestor (the cursor may be
          // inside a <span> or <b> that is itself inside an <h2>, etc.)
          let headingAncestor = element;
          while (headingAncestor && headingAncestor !== editorRef.current) {
            if (/^H[1-6]$/i.test(headingAncestor.tagName)) {
              newState.formatBlock = headingAncestor.tagName.toLowerCase();
              break;
            }
            headingAncestor = headingAncestor.parentNode;
          }

          // Get computed styles to determine current font size and family
          const computedStyle = window.getComputedStyle(element);
          
          // Map font size to our dropdown values
          const fontSize = computedStyle.fontSize;
          if (fontSize) {
            const sizeMap = {
              '8px': '1', '10px': '1', '11px': '2', '12px': '2', '13px': '3', 
              '14px': '3', '16px': '4', '18px': '4', '20px': '5', '24px': '5',
              '32px': '6', '36px': '7', '48px': '7'
            };
            
            // Check for exact matches first
            if (sizeMap[fontSize]) {
              newState.fontSize = sizeMap[fontSize];
            } else {
              // Parse numeric value for approximate matching
              const pxValue = parseInt(fontSize);
              if (pxValue <= 10) newState.fontSize = '1';
              else if (pxValue <= 12) newState.fontSize = '2';
              else if (pxValue <= 14) newState.fontSize = '3';
              else if (pxValue <= 18) newState.fontSize = '4';
              else if (pxValue <= 24) newState.fontSize = '5';
              else if (pxValue <= 32) newState.fontSize = '6';
              else newState.fontSize = '7';
            }
          }
          
          // Get font family
          const fontFamily = computedStyle.fontFamily;
          if (fontFamily) {
            // Clean up font family string and map to our options
            const cleanFamily = fontFamily.replace(/['"]/g, '').toLowerCase();
            if (cleanFamily.includes('arial')) newState.fontFamily = 'Arial';
            else if (cleanFamily.includes('helvetica')) newState.fontFamily = 'Helvetica';
            else if (cleanFamily.includes('times')) newState.fontFamily = 'Times New Roman';
            else if (cleanFamily.includes('georgia')) newState.fontFamily = 'Georgia';
            else if (cleanFamily.includes('verdana')) newState.fontFamily = 'Verdana';
            else if (cleanFamily.includes('courier')) newState.fontFamily = 'Courier New';
          }
        }
      }

      setFormatState(newState);
    } catch (e) {
      // Ignore errors - just keep current state
    }
  }, [readOnly]);

  // Handle content changes
  const handleInput = useCallback(() => {
    canUndoImageInsertRef.current = false; // any keystroke/edit clears the image-undo window
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
    updateFormatState();
  }, [onChange, updateFormatState]);

  // Handle selection changes
  const handleSelectionChange = useCallback(() => {
    updateFormatState();
  }, [updateFormatState]);

  // Keyboard handler — Backspace/Delete over images and Ctrl+Z to undo last insert
  const handleKeyDown = useCallback((e) => {
    if (readOnly) return;
    const editor = editorRef.current;
    if (!editor) return;

    // ── Ctrl+Z / Cmd+Z: undo the most recent image or amp-card insertion ──────
    // Ordinary browser undo doesn't cover our DOM-manipulated inserts, so we
    // intercept the shortcut while the insertion is still the "last action".
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      if (
        canUndoImageInsertRef.current &&
        lastInsertedNodeRef.current &&
        editor.contains(lastInsertedNodeRef.current)
      ) {
        e.preventDefault();
        lastInsertedNodeRef.current.remove();
        lastInsertedNodeRef.current = null;
        canUndoImageInsertRef.current = false;
        if (onChange && editor) onChange(editor.innerHTML);
        return;
      }
    }

    if (e.key !== 'Backspace' && e.key !== 'Delete') return;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);

    // Helper: is `node` a deletable image or amp-embed-card?
    const isDeletable = (node) =>
      node &&
      node.nodeType === Node.ELEMENT_NODE &&
      (node.tagName === 'IMG' || node.classList?.contains('amp-embed-card') || node.classList?.contains('pdf-attachment-card'));

    // ── Case 1: img/card is object-selected (browser put a single-element
    //           selection around it, e.g. after clicking on the image) ─────── 
    if (!range.collapsed && range.endOffset - range.startOffset === 1 && range.startContainer.childNodes) {
      const candidate = range.startContainer.childNodes[range.startOffset];
      if (isDeletable(candidate)) {
        e.preventDefault();
        candidate.remove();
        if (onChange && editor) onChange(editor.innerHTML);
        return;
      }
    }

    if (!range.collapsed) return; // let browser handle other non-collapsed selections

    // Walk up from the cursor to find the direct-editor-child block
    let currentBlock = null;
    let node = range.startContainer;
    while (node && node !== editor) {
      if (node.parentNode === editor) { currentBlock = node; break; }
      node = node.parentNode;
    }

    // ── Backspace ────────────────────────────────────────────────────────────
    if (e.key === 'Backspace') {
      // Cursor is bare inside the editor (no block wrapper) — index > 0
      if (!currentBlock && range.startContainer === editor && range.startOffset > 0) {
        const prev = editor.childNodes[range.startOffset - 1];
        if (isDeletable(prev)) {
          e.preventDefault();
          prev.remove();
          if (onChange && editor) onChange(editor.innerHTML);
          return;
        }
      }
      // Cursor is at the very start of a block — the preceding sibling may be an image
      if (currentBlock && isAtBlockStart(range.startContainer, range.startOffset, currentBlock)) {
        const prev = currentBlock.previousSibling;
        if (isDeletable(prev)) {
          e.preventDefault();
          prev.remove();
          if (onChange && editor) onChange(editor.innerHTML);
          return;
        }
      }
    }

    // ── Delete ───────────────────────────────────────────────────────────────
    if (e.key === 'Delete') {
      // Cursor is bare inside the editor
      if (!currentBlock && range.startContainer === editor) {
        const next = editor.childNodes[range.startOffset];
        if (isDeletable(next)) {
          e.preventDefault();
          next.remove();
          if (onChange && editor) onChange(editor.innerHTML);
          return;
        }
      }
      // Cursor is at the very end of a block — the next sibling may be an image
      if (currentBlock && isAtBlockEnd(range.startContainer, range.startOffset, currentBlock)) {
        const next = currentBlock.nextSibling;
        if (isDeletable(next)) {
          e.preventDefault();
          next.remove();
          if (onChange && editor) onChange(editor.innerHTML);
          return;
        }
      }
    }
  }, [readOnly, onChange]);

  // Set up event listeners
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.addEventListener('input', handleInput);
    document.addEventListener('selectionchange', handleSelectionChange);

    // Use <div> as the default block so Enter doesn't create <p> with margins
    document.execCommand('defaultParagraphSeparator', false, 'div');

    // Floating delete button — positioned over hovered images, no DOM wrapping
    if (!readOnly) {
      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'img-delete-btn';
      deleteBtn.type = 'button';
      deleteBtn.title = 'Remove';
      deleteBtn.textContent = '\u2715';
      deleteBtn.style.display = 'none';
      editor.style.position = 'relative';
      editor.appendChild(deleteBtn);

      // Tracks whichever deletable element (img or amp-embed-card) is hovered
      let hoveredEl = null;

      const positionBtn = (el) => {
        const editorRect = editor.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        deleteBtn.style.display = 'flex';
        deleteBtn.style.top = (elRect.top - editorRect.top + editor.scrollTop + 6) + 'px';
        deleteBtn.style.right = (editorRect.right - elRect.right + 6) + 'px';
      };

      const onMouseOver = (e) => {
        // Match <img> elements or the root .amp-embed-card/.pdf-attachment-card div
        const img = e.target.closest('img');
        const card = e.target.closest('.amp-embed-card');
        const pdfCard = e.target.closest('.pdf-attachment-card');
        const target = img || card || pdfCard;
        if (target && editor.contains(target)) {
          hoveredEl = target;
          positionBtn(target);
        }
      };
      const onMouseOut = (e) => {
        const related = e.relatedTarget;
        if (related === deleteBtn || deleteBtn.contains(related)) return;
        // Stay visible when moving within the same card
        if (hoveredEl && hoveredEl.contains(related)) return;
        if (!related || !related.closest || (!related.closest('img') && !related.closest('.amp-embed-card') && !related.closest('.pdf-attachment-card'))) {
          deleteBtn.style.display = 'none';
          hoveredEl = null;
        }
      };
      const onDeleteClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (hoveredEl) {
          hoveredEl.remove();
          hoveredEl = null;
          deleteBtn.style.display = 'none';
          if (onChange && editor) onChange(editor.innerHTML);
        }
      };
      const onDeleteOut = (e) => {
        const related = e.relatedTarget;
        if (related !== hoveredEl && (!related || !hoveredEl || !hoveredEl.contains(related))) {
          deleteBtn.style.display = 'none';
          hoveredEl = null;
        }
      };

      editor.addEventListener('mouseover', onMouseOver);
      editor.addEventListener('mouseout', onMouseOut);
      deleteBtn.addEventListener('mousedown', onDeleteClick);
      deleteBtn.addEventListener('mouseout', onDeleteOut);

      // ── PDF card click-to-open ────────────────────────────────────────────
      // Browsers suppress <a href> navigation inside a contentEditable surface,
      // even when the card itself has contenteditable="false". Intercept clicks
      // on any .pdf-attachment-card and use window.open so the PDF opens in a
      // new tab regardless of the editing context.
      const onPdfCardClick = (e) => {
        const card = e.target.closest('.pdf-attachment-card');
        if (!card) return;
        if (e.target.closest('.img-delete-btn')) return; // let delete handle it
        const link = card.querySelector('a[href]');
        if (!link) return;
        window.open(link.href, '_blank', 'noopener,noreferrer');
      };
      editor.addEventListener('click', onPdfCardClick);

      // ── Card drag-to-reposition ───────────────────────────────────────────
      // Dragging an <img> inside a .pdf-attachment-card or .amp-embed-card
      // normally causes the browser to split the card — it moves only the <img>
      // element and leaves the card shell behind. These handlers intercept that
      // drag and redirect it to move the entire card (or a standalone <img> that
      // is a direct editor child) to the cursor position.
      //
      // Within long text blocks the card can be placed at any line: the block
      // is split at the caret position (caretRangeFromPoint / caretPositionFromPoint)
      // and the card is inserted between the two halves.
      //
      // External file drops (images/PDFs from the filesystem) are not affected:
      // draggingCard is null, so the handlers return early and the event bubbles
      // to React's handleDrop / handleDragOver.

      let draggingCard = null;

      // Blue indicator line — fixed-positioned so it can appear at any Y,
      // even mid-paragraph.  Appended to body so it is unaffected by editor
      // float layout.
      const dropLine = document.createElement('div');
      dropLine.setAttribute('contenteditable', 'false');
      dropLine.style.cssText =
        'position:fixed;height:3px;background:#2563eb;border-radius:2px;' +
        'pointer-events:none;display:none;z-index:9999;left:0;width:0';
      document.body.appendChild(dropLine);

      // Walk up from node to find the direct child of editor, or null
      const getDirectChild = (node) => {
        let cur = node;
        while (cur && cur !== editor) {
          if (cur.parentNode === editor) return cur;
          cur = cur.parentNode;
        }
        return null;
      };

      // Cross-browser collapsed Range at the given viewport coordinates.
      const getCaretRange = (x, y) => {
        if (document.caretRangeFromPoint) return document.caretRangeFromPoint(x, y);
        if (document.caretPositionFromPoint) {
          const pos = document.caretPositionFromPoint(x, y);
          if (!pos) return null;
          const r = document.createRange();
          r.setStart(pos.offsetNode, pos.offset);
          r.collapse(true);
          return r;
        }
        return null;
      };

      // Blocks taller than this threshold get line-level split resolution.
      const SPLIT_MIN_HEIGHT = 40;

      // Returns { insertBefore, splitRange, lineY }:
      //   insertBefore — direct editor child to insert before (null = append)
      //   splitRange   — Range inside insertBefore to split at; null = no split
      //   lineY        — viewport Y coordinate for the drop indicator
      const findDropPosition = (clientX, clientY) => {
        const children = Array.from(editor.childNodes).filter(
          n => n !== draggingCard && n !== dropLine && n.nodeType === Node.ELEMENT_NODE
        );

        for (let i = 0; i < children.length; i++) {
          const child = children[i];
          const rect = child.getBoundingClientRect();

          if (clientY < rect.top) {
            return { insertBefore: child, splitRange: null, lineY: rect.top };
          }

          if (clientY <= rect.bottom) {
            const isTextBlock =
              !child.classList.contains('pdf-attachment-card') &&
              !child.classList.contains('amp-embed-card') &&
              child.tagName !== 'IMG';

            if (isTextBlock && rect.height > SPLIT_MIN_HEIGHT) {
              const range = getCaretRange(clientX, clientY);
              if (range && child.contains(range.startContainer)) {
                const rr = range.getBoundingClientRect();
                const lineH = rr.height > 0 ? rr.height : 20;
                const lineTop = rr.top > 0 ? rr.top : clientY;

                // First line → insert before the entire block
                if (lineTop <= rect.top + lineH) {
                  return { insertBefore: child, splitRange: null, lineY: rect.top };
                }
                // Last line → insert after the entire block
                if (lineTop >= rect.bottom - lineH * 2) {
                  return { insertBefore: children[i + 1] || null, splitRange: null, lineY: rect.bottom };
                }
                // Middle of block → split at caret position
                return { insertBefore: child, splitRange: range, lineY: lineTop };
              }
            }

            // Fallback: top vs bottom half of block
            if (clientY < rect.top + rect.height / 2) {
              return { insertBefore: child, splitRange: null, lineY: rect.top };
            }
            return { insertBefore: children[i + 1] || null, splitRange: null, lineY: rect.bottom };
          }
        }

        const last = children[children.length - 1];
        return {
          insertBefore: null,
          splitRange: null,
          lineY: last ? last.getBoundingClientRect().bottom : editor.getBoundingClientRect().top,
        };
      };

      const onCardDragStart = (e) => {
        const target = e.target;
        // Identify a managed card or a direct-child <img>
        const managed =
          target.closest?.('.pdf-attachment-card') ||
          target.closest?.('.amp-embed-card') ||
          (target.tagName === 'IMG' && target.parentNode === editor ? target : null);
        if (!managed || !editor.contains(managed)) return;

        const topLevel = getDirectChild(managed);
        if (!topLevel) return;

        draggingCard = topLevel;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('application/x-rte-card', '1');
        try { e.dataTransfer.setDragImage(topLevel, 12, 12); } catch (_) {}
        // Hide delete button while dragging
        deleteBtn.style.display = 'none';
      };

      const onCardDragOver = (e) => {
        if (!draggingCard) return;
        if (!e.dataTransfer?.types?.includes('application/x-rte-card')) return;
        e.preventDefault();
        e.stopPropagation(); // prevent React's handleDragOver from firing
        e.dataTransfer.dropEffect = 'move';

        const { lineY } = findDropPosition(e.clientX, e.clientY);
        const editorRect = editor.getBoundingClientRect();
        dropLine.style.top = (lineY - 1) + 'px';
        dropLine.style.left = editorRect.left + 'px';
        dropLine.style.width = editorRect.width + 'px';
        dropLine.style.display = 'block';
      };

      const onCardDrop = (e) => {
        if (!draggingCard) return;
        if (!e.dataTransfer?.types?.includes('application/x-rte-card')) return;
        e.preventDefault();
        e.stopPropagation(); // prevent React's handleDrop from firing

        const { insertBefore, splitRange } = findDropPosition(e.clientX, e.clientY);

        if (splitRange) {
          const blockToSplit = getDirectChild(splitRange.startContainer);
          if (blockToSplit && blockToSplit !== draggingCard) {
            // Extract content from caret position to end of block
            const endRange = document.createRange();
            endRange.setStart(splitRange.startContainer, splitRange.startOffset);
            endRange.setEnd(blockToSplit, blockToSplit.childNodes.length);
            const fragment = endRange.extractContents();

            // Wrap extracted tail in a new block of the same tag
            const newBlock = document.createElement(blockToSplit.tagName.toLowerCase());
            newBlock.appendChild(fragment);

            const after = blockToSplit.nextSibling;
            if (newBlock.textContent !== '' || newBlock.querySelector('img,br')) {
              editor.insertBefore(newBlock, after);
              editor.insertBefore(draggingCard, newBlock);
            } else {
              // Nothing after split point — append card after the block
              editor.insertBefore(draggingCard, after);
            }
            // Remove first half if the split left it empty
            if (blockToSplit.textContent === '' && !blockToSplit.querySelector('img,br')) {
              blockToSplit.remove();
            }
          } else {
            // Fallback to block-level positioning
            if (insertBefore && insertBefore !== draggingCard) editor.insertBefore(draggingCard, insertBefore);
            else editor.appendChild(draggingCard);
          }
        } else {
          if (insertBefore && insertBefore !== draggingCard) editor.insertBefore(draggingCard, insertBefore);
          else if (!insertBefore) editor.appendChild(draggingCard);
        }

        dropLine.style.display = 'none';
        draggingCard = null;
        if (onChange && editor) onChange(editor.innerHTML);
      };

      const onCardDragEnd = () => {
        dropLine.style.display = 'none';
        draggingCard = null;
      };

      editor.addEventListener('dragstart', onCardDragStart);
      editor.addEventListener('dragover', onCardDragOver);
      editor.addEventListener('drop', onCardDrop);
      editor.addEventListener('dragend', onCardDragEnd);

      return () => {
        editor.removeEventListener('input', handleInput);
        document.removeEventListener('selectionchange', handleSelectionChange);
        editor.removeEventListener('mouseover', onMouseOver);
        editor.removeEventListener('mouseout', onMouseOut);
        editor.removeEventListener('click', onPdfCardClick);
        editor.removeEventListener('dragstart', onCardDragStart);
        editor.removeEventListener('dragover', onCardDragOver);
        editor.removeEventListener('drop', onCardDrop);
        editor.removeEventListener('dragend', onCardDragEnd);
        deleteBtn.remove();
        dropLine.remove();
      };
    }

    return () => {
      editor.removeEventListener('input', handleInput);
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleInput, handleSelectionChange]); // onChange and readOnly are stable for the editor's lifetime

  // Apply heading/block style using formatBlock.
  // Works with collapsed cursor (style-first-then-type) and with selected text.
  const applyHeadingStyle = useCallback((headingType) => {
    if (readOnly) return;
    editorRef.current?.focus();
    // The <select> dropdown steals focus before onChange fires. Restore the
    // saved selection so formatBlock applies to where the user was typing,
    // not the beginning of the editor.
    if (savedSelectionRef.current) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(savedSelectionRef.current);
      }
      savedSelectionRef.current = null;
    }

    if (headingType === 'normal') {
      document.execCommand('formatBlock', false, 'div');
    } else {
      document.execCommand('formatBlock', false, headingType);
    }

    setTimeout(updateFormatState, 10);
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [readOnly, onChange, updateFormatState]);

  // Simple format function for other commands
  const formatText = useCallback((command, value = null) => {
    if (readOnly) return;
    editorRef.current?.focus();

    // Just execute the command - let browser handle selection naturally
    document.execCommand(command, false, value);
    
    // Update format state after command
    setTimeout(updateFormatState, 10);
    
    // Trigger onChange
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [readOnly, onChange, updateFormatState]);

  // Shared helper — inserts an img element at the cursor and fires onChange.
  // Images are floated right (CSS), so they must live OUTSIDE block elements
  // like <p> to allow text from subsequent paragraphs to wrap on the left
  // (Wikipedia-style layout). If the cursor is inside a <p>/<div>/<li>, the
  // image is placed just before that block element as a sibling.
  const insertImgNode = useCallback((img) => {
    const editor = editorRef.current;
    editor.focus();
    const selection = window.getSelection();

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      let container = range.startContainer;

      // Walk up to find the nearest block-level ancestor that is a direct
      // child of the editor (e.g. the <p> or <div> the cursor sits in).
      let blockParent = null;
      let node = container.nodeType === Node.TEXT_NODE ? container.parentNode : container;
      while (node && node !== editor) {
        if (node.parentNode === editor) {
          blockParent = node;
          break;
        }
        node = node.parentNode;
      }

      if (blockParent) {
        if (/^H[1-6]$/i.test(blockParent.tagName)) {
          // Headings have clear:both — inserting before them would leave a float
          // gap. Instead insert after the heading so the image floats within the
          // following section body and text wraps around it on the left.
          editor.insertBefore(img, blockParent.nextSibling || null);
        } else {
          // Insert the image before the block element so it floats freely
          editor.insertBefore(img, blockParent);
        }
      } else {
        // Cursor is directly in the editor (no block wrapper) — just insert
        range.deleteContents();
        range.insertNode(img);
      }

      // Move cursor after the image
      const newRange = document.createRange();
      newRange.setStartAfter(img);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    } else {
      editor.appendChild(img);
    }

    // Record this insertion so Ctrl+Z can undo it
    lastInsertedNodeRef.current = img;
    canUndoImageInsertRef.current = true;

    if (onChange && editor) {
      onChange(editor.innerHTML);
    }
  }, [onChange]);

  // Open the artifact gallery picker — fetches image artifacts already uploaded for this amp
  const openArtifactPicker = useCallback(async () => {
    if (!amplifierId) return;
    setShowArtifactPicker(true);
    setPickerLoading(true);
    try {
      const resp = await fetch(`${API_URL}/amplifier_artifacts?amplifier_id=${amplifierId}`);
      if (resp.ok) {
        const data = await resp.json();
        // Only show image artifacts in the picker
        setPickerArtifacts((data || []).filter(a => a.mime_type && a.mime_type.startsWith('image/')));
      }
    } catch (_) {}
    setPickerLoading(false);
  }, [amplifierId]);

  const pickArtifact = useCallback((artifact) => {
    const img = document.createElement('img');
    img.src = `${API_URL}/amplifier_artifacts/${artifact.artifact_id}/thumbnail`;
    img.setAttribute('data-artifact-id', String(artifact.artifact_id));
    img.className = 'wiki-artifact-thumb';
    img.alt = artifact.filename || 'Image';
    insertImgNode(img);
    setShowArtifactPicker(false);
  }, [insertImgNode]);

  // Fetch amp data and replace the loading placeholder with a rich embed card.
  // Falls back to a plain link when the amp is not found or the fetch fails.
  const insertAmpCard = useCallback(async (mfrSlug, ampSlug, placeholderId) => {
    const ampUrl = `https://ampnebula.com/amps/${mfrSlug}/${ampSlug}`;
    const replace = (html) => {
      const ph = editorRef.current?.querySelector(`#${placeholderId}`);
      if (!ph) return;
      // Parse the html into a real DOM node and swap it in — avoids the
      // outerHTML-assignment behaviour that re-creates the element and can
      // re-trigger browser content-model fixup.
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const card = tmp.firstElementChild;
      if (card) {
        ph.replaceWith(card);
        // Update the undo ref — the placeholder has been swapped out, point at the real card
        lastInsertedNodeRef.current = card;
        canUndoImageInsertRef.current = true;
      } else {
        ph.outerHTML = html;
      }
      if (onChange && editorRef.current) onChange(editorRef.current.innerHTML);
    };
    try {
      const resp = await fetch(
        `${API_URL}/amplifiers/by-slug/${encodeURIComponent(mfrSlug)}/${encodeURIComponent(ampSlug)}`
      );
      if (!resp.ok) throw new Error('not found');
      const amp = await resp.json();

      // Determine thumbnail: prefer pinned artifact, then fall back to the
      // first uploaded photo for this amp.
      let thumbPath = null;
      if (amp.pinned_artifact_id) {
        thumbPath = `${API_URL}/amplifier_artifacts/${amp.pinned_artifact_id}/thumbnail`;
      } else {
        // No pinned artifact — try to find any photo for this amp
        try {
          const photosResp = await fetch(
            `${API_URL}/amplifier_artifacts?amplifier_id=${amp.amplifier_id}&artifact_type=photo`
          );
          if (photosResp.ok) {
            const photos = await photosResp.json();
            if (photos.length > 0) {
              thumbPath = `${API_URL}/amplifier_artifacts/${photos[0].artifact_id}/thumbnail`;
            }
          }
        } catch { /* no photos — leave thumbPath null */ }
      }

      const yearStr = amp.start_year
        ? (amp.end_year && amp.end_year !== amp.start_year
            ? `${amp.start_year}\u2013${amp.end_year}`
            : String(amp.start_year))
        : '';
      const ampName = escHtml(amp.name || amp.model || 'Amplifier');
      const mfrName = escHtml(amp.manufacturer_name || '');

      const imgHtml = thumbPath
        ? `<img src="${escHtml(thumbPath)}" class="amp-embed-thumb" alt="${mfrName} ${ampName}" onerror="this.style.display='none'" />`
        : `<span class="amp-embed-no-img">&#127928;</span>`;

      // The placeholder was inserted as a direct child of the editor (before
      // the current paragraph) via insertImgNode, so the card here can safely
      // use block-level <div> elements — they are never inside a <p>.
      replace(
        `<div class="amp-embed-card" data-amp-embed="${escHtml(mfrSlug)}/${escHtml(ampSlug)}" contenteditable="false">` +
          `<a href="${escHtml(ampUrl)}" target="_blank" rel="noopener noreferrer" class="amp-embed-link">` +
            `<div class="amp-embed-img-wrap">${imgHtml}</div>` +
            `<div class="amp-embed-body">` +
              `<div class="amp-embed-mfr">${mfrName}</div>` +
              `<div class="amp-embed-name">${ampName}</div>` +
              (yearStr ? `<div class="amp-embed-year">${yearStr}</div>` : '') +
            `</div>` +
          `</a>` +
        `</div>`
      );
    } catch {
      replace(`<a href="${escHtml(ampUrl)}" target="_blank" rel="noopener noreferrer">${escHtml(ampUrl)}</a>`);
    }
  }, [onChange]);

  // Insert a hyperlink at the current selection.
  // Only http/https URLs are accepted (prevents javascript: injection).
  const insertLink = useCallback(() => {    if (readOnly) return;
    const url = prompt('Enter link URL:');
    if (!url || !url.trim()) return;
    const cleanUrl = url.trim();
    if (!/^https?:\/\//i.test(cleanUrl)) {
      alert('Only http:// and https:// links are allowed.');
      return;
    }
    editorRef.current?.focus();
    document.execCommand('createLink', false, cleanUrl);
    // Ensure every anchor in the editor opens safely in a new tab
    editorRef.current?.querySelectorAll('a[href]').forEach(a => {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    });
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [readOnly, onChange]);

  // Insert image from URL
  const insertImageFromURL = useCallback(() => {
    if (readOnly) return;
    const url = prompt('Enter image URL:');
    if (url && url.trim()) {
      const img = document.createElement('img');
      img.src = url.trim();
      img.alt = 'Inserted image';
      insertImgNode(img);
    }
  }, [readOnly, insertImgNode]);

  // Shared helper: given a File, either upload it as an artifact (wiki inline embed)
  // or embed as base64 (fallback when no amplifierId context is available).
  const processImageFile = useCallback(async (file) => {
    if (amplifierId && token) {
      // Upload as artifact — server resizes if >5 MB and generates a 240px thumbnail
      setImageUploading(true);
      try {
        const formData = new FormData();
        formData.append('artifact', file);
        formData.append('amplifier_id', amplifierId);
        formData.append('artifact_type', 'photo');
        const resp = await fetch(`${API_URL}/amplifier_artifacts`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          alert(data.error || 'Image upload failed. Please try again.');
          return;
        }
        const artifact = await resp.json();
        const img = document.createElement('img');
        img.src = `${API_URL}/amplifier_artifacts/${artifact.artifact_id}/thumbnail`;
        img.setAttribute('data-artifact-id', String(artifact.artifact_id));
        img.className = 'wiki-artifact-thumb';
        img.alt = file.name;
        insertImgNode(img);
      } catch (err) {
        alert('Image upload failed: ' + err.message);
      } finally {
        setImageUploading(false);
      }
    } else {
      // Fallback: embed as base64 when no amplifierId context is available
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = document.createElement('img');
        img.src = event.target.result;
        img.alt = file.name;
        insertImgNode(img);
      };
      reader.readAsDataURL(file);
    }
  }, [amplifierId, token, insertImgNode]);

  // Insert image from file (toolbar button).
  const insertImageFromFile = useCallback(() => {
    if (readOnly) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) processImageFile(file);
    };
    input.click();
  }, [readOnly, processImageFile]);

  // Upload a PDF and insert a styled card that links to it.
  // Uses the /discussion-file-attachments endpoint for all editor contexts.
  const processPdfFile = useCallback(async (file) => {
    if (!file || file.type !== 'application/pdf') return;

    const authToken = safeStorage.getItem('authToken');
    if (!authToken) {
      alert('You must be logged in to attach PDF files.');
      return;
    }

    const PDF_MAX_BYTES = 20 * 1024 * 1024;
    if (file.size > PDF_MAX_BYTES) {
      alert('PDF files must be less than 20 MB.');
      return;
    }

    setPdfUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await fetch(`${API_URL}/discussion-file-attachments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData,
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        alert(data.error || 'PDF upload failed. Please try again.');
        return;
      }
      const { file_id, filename, file_size } = await resp.json();
      const fileUrl = `${API_URL}/discussion-file-attachments/${file_id}`;
      const thumbUrl = `${API_URL}/discussion-file-attachments/${file_id}/thumbnail`;
      const sizeStr = file_size < 1024 * 1024
        ? `${Math.round(file_size / 1024)} KB`
        : `${(file_size / (1024 * 1024)).toFixed(1)} MB`;

      const card = document.createElement('div');
      card.className = 'pdf-attachment-card';
      card.setAttribute('contenteditable', 'false');
      card.setAttribute('data-file-id', String(file_id));
      card.innerHTML =
        `<a href="${escHtml(fileUrl)}" target="_blank" rel="noopener noreferrer" class="pdf-card-link">` +
          `<div class="pdf-card-preview">` +
            `<img src="${escHtml(thumbUrl)}" class="pdf-card-thumb" alt="${escHtml(filename)}" onerror="this.parentNode.innerHTML='<span class=\\'pdf-card-fallback\\'>📄</span>'" />` +
          `</div>` +
          `<div class="pdf-card-footer">` +
            `<span class="pdf-card-name">${escHtml(filename)}</span>` +
            `<span class="pdf-card-size">${escHtml(sizeStr)}</span>` +
          `</div>` +
        `</a>`;
      insertImgNode(card);
    } catch (err) {
      alert('PDF upload failed: ' + err.message);
    } finally {
      setPdfUploading(false);
    }
  }, [insertImgNode]);

  // Open file picker for PDFs (toolbar button).
  const insertPdfFromFile = useCallback(() => {
    if (readOnly) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) processPdfFile(file);
    };
    input.click();
  }, [readOnly, processPdfFile]);

  // Intercept paste events containing image data.
  // - If onPasteImage is provided (discussion editors), raster images are routed
  //   to the parent's attachment slot.
  // - SVGs stay inline in the editor so they behave like rich-text content.
  // - Otherwise (wiki editor): upload/embed the image inline via processImageFile.
  // Also detects AmpNebula amp URLs (https://ampnebula.com/amps/:mfr/:slug) and
  // replaces them with a rich embed card.
  const handlePaste = useCallback((e) => {
    if (readOnly) return;

    // Detect AmpNebula amp URLs and real image files, in priority order:
    //
    //   1. text/plain  – direct URL / address-bar copy (high confidence)
    //   2. text/uri-list – used by some browsers when copying a hyperlink
    //   3. Real image file items — checked BEFORE text/html so that copying an
    //      image from an amp page (whose text/html wraps the <img> in an
    //      <a href="…/amps/mfr/slug"> anchor) is treated as an image paste
    //      rather than falsely triggering the amp-card path.
    //   4. href in text/html – lowest confidence; only reached when no real
    //      image file is present. Covers formatted hyperlinks where text/plain
    //      holds the link label instead of the URL.
    const cd = e.clipboardData;
    let ampMatch = null;

    const plain = cd?.getData('text/plain')?.trim();
    if (plain) ampMatch = plain.match(AMP_URL_PATTERN);

    if (!ampMatch) {
      const uriList = cd?.getData('text/uri-list')?.trim();
      if (uriList) {
        for (const line of uriList.split(/\r?\n/)) {
          const uri = line.trim();
          if (!uri || uri.startsWith('#')) continue;
          ampMatch = uri.match(AMP_URL_PATTERN);
          if (ampMatch) break;
        }
      }
    }

    // Image paste — only real file items (kind === 'file') to avoid treating
    // base64-encoded image strings as uploads. Checked before text/html so a
    // real image takes priority over any <a href> that wraps it on the source page.
    const items = cd?.items;
    if (!ampMatch && items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file' && items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile();
          if (!file) continue;
          e.preventDefault();
          if (onPasteImage && !isSvgImageFile(file)) {
            onPasteImage(file);
          } else {
            processImageFile(file);
          }
          return;
        }
      }
    }

    // text/html href — only reached when no real image file was in the clipboard
    if (!ampMatch) {
      const html = cd?.getData('text/html')?.trim();
      if (html) {
        const hrefMatch = html.match(/href=["']([^"']+)["']/i);
        if (hrefMatch) ampMatch = hrefMatch[1].match(AMP_URL_PATTERN);
      }
    }

    if (ampMatch) {
      e.preventDefault();
      editorRef.current?.focus();
      const placeholderId = `amp-loading-${Date.now()}`;
      // Insert placeholder as a real DOM node using the same placement logic
      // as insertImgNode — goes BEFORE the current block paragraph as a direct
      // editor child, so the eventual card <div> is never trapped inside a <p>.
      const placeholder = document.createElement('div');
      placeholder.id = placeholderId;
      placeholder.className = 'amp-embed-loading';
      placeholder.setAttribute('contenteditable', 'false');
      placeholder.textContent = '\u23F3\u00A0Loading amp card\u2026';
      insertImgNode(placeholder);
      insertAmpCard(ampMatch[1], ampMatch[2], placeholderId);
    }
  }, [readOnly, onPasteImage, processImageFile, insertAmpCard, insertImgNode]);

  // Handle image drag-and-drop onto the editor — mirrors the paste image path.
  const handleDrop = useCallback((e) => {
    if (readOnly) return;
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (file.type === 'application/pdf') {
      e.preventDefault();
      processPdfFile(file);
      return;
    }
    if (!file.type.startsWith('image/')) return;
    e.preventDefault();
    if (onPasteImage && !isSvgImageFile(file)) {
      onPasteImage(file);
    } else {
      processImageFile(file);
    }
  }, [readOnly, onPasteImage, processImageFile, processPdfFile]);

  // Allow drops by preventing the default dragover behaviour (which blocks drops).
  const handleDragOver = useCallback((e) => {
    if (readOnly) return;
    const hasAccepted = Array.from(e.dataTransfer?.items || []).some(
      item => item.kind === 'file' && (item.type.startsWith('image/') || item.type === 'application/pdf')
    );
    if (hasAccepted) e.preventDefault();
  }, [readOnly]);

  // Prevent toolbar buttons from stealing focus
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
  }, []);

  return (
    <div className="rich-text-editor">
      {!readOnly && (
        <>
        <div className="editor-toolbar">
          <div className="toolbar-group">
            <select 
              onMouseDown={() => {
                // Save selection NOW before the dropdown steals focus from the editor
                const sel = window.getSelection();
                if (sel && sel.rangeCount > 0) {
                  savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
                }
              }}
              onChange={(e) => {
                if (e.target.value === '') {
                  applyHeadingStyle('normal');
                } else {
                  applyHeadingStyle(e.target.value);
                }
              }}
              value={formatState.formatBlock === 'div' ? '' : formatState.formatBlock}
              title="Text Style"
            >
              <option value="">Normal</option>
              <option value="h1">Heading 1</option>
              <option value="h2">Heading 2</option>
              <option value="h3">Heading 3</option>
              <option value="h4">Heading 4</option>
              <option value="h5">Heading 5</option>
              <option value="h6">Heading 6</option>
            </select>
          </div>

          <div className="toolbar-group">
            <select 
              onChange={(e) => {
                if (e.target.value) {
                  formatText('fontSize', e.target.value);
                }
              }}
              value={formatState.fontSize}
              title="Font Size"
            >
              <option value="">Font Size</option>
              <option value="1">8pt</option>
              <option value="2">10pt</option>
              <option value="3">12pt</option>
              <option value="4">14pt</option>
              <option value="5">18pt</option>
              <option value="6">24pt</option>
              <option value="7">36pt</option>
            </select>
          </div>

          <div className="toolbar-group">
            <select 
              onChange={(e) => {
                if (e.target.value) {
                  formatText('fontName', e.target.value);
                }
              }}
              value={formatState.fontFamily}
              title="Font Family"
            >
              <option value="">Font Family</option>
              <option value="Arial">Arial</option>
              <option value="Helvetica">Helvetica</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Georgia">Georgia</option>
              <option value="Verdana">Verdana</option>
              <option value="Courier New">Courier New</option>
            </select>
          </div>
          
          <div className="toolbar-group">
            <button 
              type="button" 
              onMouseDown={handleMouseDown}
              onClick={() => formatText('bold')}
              title="Bold"
              className={formatState.bold ? 'active' : ''}
            >
              <strong>B</strong>
            </button>
            
            <button 
              type="button" 
              onMouseDown={handleMouseDown}
              onClick={() => formatText('italic')}
              title="Italic"
              className={formatState.italic ? 'active' : ''}
            >
              <em>I</em>
            </button>
            
            <button 
              type="button" 
              onMouseDown={handleMouseDown}
              onClick={() => formatText('underline')}
              title="Underline"
              className={formatState.underline ? 'active' : ''}
            >
              <span style={{ textDecoration: 'underline' }}>U</span>
            </button>
          </div>

          <div className="toolbar-group">
            <button 
              type="button" 
              onMouseDown={handleMouseDown}
              onClick={() => formatText('insertUnorderedList')}
              title="Bullet List"
            >
              • List
            </button>
            
            <button 
              type="button" 
              onMouseDown={handleMouseDown}
              onClick={() => formatText('insertOrderedList')}
              title="Numbered List"
            >
              1. List
            </button>
          </div>

          <div className="toolbar-group">
            <button 
              type="button" 
              onMouseDown={handleMouseDown}
              onClick={() => formatText('justifyLeft')}
              title="Align Left"
            >
              ←
            </button>
            
            <button 
              type="button" 
              onMouseDown={handleMouseDown}
              onClick={() => formatText('justifyCenter')}
              title="Align Center"
            >
              ↔
            </button>
            
            <button 
              type="button" 
              onMouseDown={handleMouseDown}
              onClick={() => formatText('justifyRight')}
              title="Align Right"
            >
              →
            </button>
          </div>

          <div className="toolbar-group">
            <button 
              type="button" 
              onMouseDown={handleMouseDown}
              onClick={insertLink}
              title="Insert Link"
            >
              🔗 Link
            </button>

            <button 
              type="button" 
              onMouseDown={handleMouseDown}
              onClick={insertImageFromURL}
              title="Insert Image from URL"
            >
              Image URL
            </button>
            
            <button 
              type="button" 
              onMouseDown={handleMouseDown}
              onClick={insertImageFromFile}
              disabled={imageUploading}
              title="Upload a new image file"
              style={imageUploading ? { opacity: 0.6, cursor: 'wait' } : undefined}
            >
              {imageUploading ? '⏳ Uploading…' : '📎 Upload Image'}
            </button>

            <button
              type="button"
              onMouseDown={handleMouseDown}
              onClick={insertPdfFromFile}
              disabled={pdfUploading}
              title="Upload a PDF document"
              style={pdfUploading ? { opacity: 0.6, cursor: 'wait' } : undefined}
            >
              {pdfUploading ? '⏳ Uploading…' : '📄 Upload PDF'}
            </button>

            {amplifierId && (
              <button
                type="button"
                onMouseDown={handleMouseDown}
                onClick={openArtifactPicker}
                title="Choose from existing amp photos"
              >
                🖼 Gallery
              </button>
            )}
          </div>
        </div>

        {/* Artifact gallery picker panel */}
        {showArtifactPicker && (
          <div style={{
            position: 'relative',
            zIndex: 10,
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            background: 'white',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            padding: '0.75rem',
            marginBottom: '0.5rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>
                Choose from existing photos
              </span>
              <button
                type="button"
                onClick={() => setShowArtifactPicker(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#9ca3af', lineHeight: 1 }}
                title="Close"
              >
                ✕
              </button>
            </div>

            {pickerLoading ? (
              <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: '0.5rem 0' }}>Loading…</p>
            ) : pickerArtifacts.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: '0.5rem 0' }}>
                No photos uploaded yet. Use "Upload Image" to add one.
              </p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', maxHeight: '200px', overflowY: 'auto' }}>
                {pickerArtifacts.map(artifact => (
                  <button
                    key={artifact.artifact_id}
                    type="button"
                    onClick={() => pickArtifact(artifact)}
                    title={artifact.filename}
                    style={{
                      padding: 0,
                      border: '2px solid transparent',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      background: 'none',
                      transition: 'border-color 0.15s',
                      outline: 'none',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#2563eb'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
                  >
                    <img
                      src={`${API_URL}/amplifier_artifacts/${artifact.artifact_id}/thumbnail`}
                      alt={artifact.filename}
                      style={{ width: 72, height: 72, objectFit: 'cover', display: 'block', borderRadius: '2px' }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        </>
      )}
      
      <div
        ref={editorRef}
        className={`editor-content amp-description-display ${readOnly ? 'readonly' : ''}`}
        contentEditable={!readOnly}
        suppressContentEditableWarning={true}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        style={{
          minHeight: '200px',
          padding: '12px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          outline: 'none',
          backgroundColor: readOnly ? '#f5f5f5' : 'white',
          direction: 'ltr', // Force left-to-right text direction
          textAlign: 'left', // Force left alignment
          lineHeight: '1.5',
          fontSize: '14px',
          fontFamily: 'Arial, sans-serif'
        }}
      />
    </div>
  );
};

export default RichTextEditor;